import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { AgFilterComponent, CustomFilterParams } from '../ag-filter.component';
import { cleanStringForComparison } from '../../../../core/utilities';
import { Observable, of } from 'rxjs';
import { IAfterGuiAttachedParams, IDoesFilterPassParams } from 'ag-grid-community';
import { MultiSelectWithSearchComponent } from '../../multi-select-with-search/multi-select-with-search.component';

@Component({
    selector: 'app-multiselect-filter',
    templateUrl: './ag-multi-select-filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
  imports: [
    MultiSelectWithSearchComponent,
  ],
})
export class AgMultiSelectFilterComponent extends AgFilterComponent {
  @ViewChild(MultiSelectWithSearchComponent) multiSelectWithSearch: MultiSelectWithSearchComponent;

  filterOptions: Observable<{ name, value }[]>;

  gridContainerHeightPx: number = null;

  override get filterValue(): string[] {
    return this.multiSelectWithSearch.getSelection();
  }

  constructor() {
    super();
  }

  // class method overrides

  override agInit(params: CustomFilterParams) {
    super.agInit(params);
    this.initFilterOptions();
  }

  override setModel(model: string[] | null) {
    this.multiSelectWithSearch.setSelection(model);
  }

  override doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (!this.isFilterActive()) {
      return true; // early return, filter is inactive
    }

    // get the value for this filter's column for the provided row data
    // https://www.ag-grid.com/angular-data-grid/component-floating-filter/
    // see Custom Filter And Custom Floating Filter for reference
    const { node } = params;
    const value = this.params.getValue(node);


    // cleaned filter dropdown options
    const cleanedFilterValues = this.filterValue.map((item) => cleanStringForComparison(item));

    const filterCb = this.params?.filterCb ?? ((filterValues: any, rowValue: any) => {
      const isValueArray = Array.isArray(rowValue);
      const validStrings = this.params?.validStrings ?? [];

      // Handle Array Values
      if (isValueArray) {
        return rowValue.some((item: string) =>
            cleanedFilterValues.includes(cleanStringForComparison(item)) ||
            validStrings.includes(item),
        );
      }

      // Handle String Values
      const cleanRowValue = cleanStringForComparison(rowValue);
      const matchesFilter = cleanedFilterValues.some(f => cleanRowValue.includes(f));
      const matchesValidStr = validStrings.some(s => cleanRowValue.includes(s));

      return matchesFilter || matchesValidStr;
    });

    return value && filterCb(this.filterValue, value);
  }

  private initFilterOptions() {
    // we'll be working with a data stream
    this.filterOptions =
        this.params.filterOptions instanceof Observable ? // determine type
            this.params.filterOptions :
            of(this.params.filterOptions); // convert to observable data stream if needed

  };

  /**
   * Used to compute max dropdown height. Make sure to define gridContainerId in filterParams of
   * respective grid.
   * @private
   */
  private getGridContainerHeightPx(): number {
    const el = document.getElementById(this.params?.gridContainerId);
    return el?.clientHeight;
  }

  /**
   * Called by floating filter's reset button.
   */
  handleReset() {
    this.multiSelectWithSearch?.resetSelection();
  }

  /**
   * Reset the search input every time the filter menu is closed.
   */
  afterGuiDetached() {
    this.multiSelectWithSearch?.resetSearch();
  }

  /**
   * Init gridContainerHeightPx
   * Virtual scroll container might not know it's height, so recalculate once it's rendered.
   */
  afterGuiAttached(params?: IAfterGuiAttachedParams) {
    this.gridContainerHeightPx = this.getGridContainerHeightPx();
    setTimeout(() => {
      this.multiSelectWithSearch.checkViewportSize();
    });
  }

}
